import moment from 'moment';

import { isUnreservedCompanyPrefix, isWellFormedCompanyPrefix, isWellFormedCompanyWebsite } from './validation';

export function init(): void {
  addUnobtrusiveAdapter('xbrlvaliddate', ['dateformat', 'mindate', 'maxdate']);
  addUnobtrusiveAdapter('minlength', ['min']);
  addUnobtrusiveAdapter('equalsto', ['validateempty', 'other']);
  addUnobtrusiveAdapter('callotherclient', ['validateempty', 'other', 'maxdate']);
  addUnobtrusiveAdapter('endafterstart', ['validateempty', 'start', 'end']);
  addUnobtrusiveAdapter('uniquefilingnamebyfiler', ['fileridfield']);
  addUnobtrusiveAdapter('clientuniquefilername', []);
  addUnobtrusiveAdapter('wellformedcompanywebsite', []);
  addUnobtrusiveAdapter('wellformedcompanyprefix', []);
  addUnobtrusiveAdapter('unreservedcompanyprefix', []);
  addUnobtrusiveAdapter('shrequiredif', ['base', 'other', 'otherexpectedvalue']);
  addUnobtrusiveAdapter('validcrn', ['other']);
  addUnobtrusiveAdapter('noclientvalidation', []);
  addUnobtrusiveAdapter('shrequired', []);
  addUnobtrusiveAdapter('shregex', ['pattern']);
  addUnobtrusiveAdapter('shregexpassword', ['pattern']);
  addUnobtrusiveAdapter('requiredifnotempty', ['other']);
  addUnobtrusiveAdapter('iftruethenendafterstart', ['booleanfield', 'validateempty', 'start', 'end']);
  addUnobtrusiveAdapter('iftruethencallotherclient', ['booleanfield', 'validateempty', 'other', 'maxdate']);
  addUnobtrusiveAdapter('iftruethenxbrlvaliddate', ['booleanfield', 'dateformat', 'mindate', 'maxdate']);
  addUnobtrusiveAdapter('shrequiredskip', ['pattern']);
  addUnobtrusiveAdapter('validrole', ['rolefield']);
  addUnobtrusiveAdapter('atleastonechecked', ['checkboxesselector']);
  addUnobtrusiveAdapter('noduplicateentitytypes', []);

  // Patch foolproof core's validator to fix whitespace-only and passOnNull handling
  window.foolproof = window.foolproof || {};
  window.foolproof.is = fixedFoolproofIs;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fixedFoolproofIs(value1: any, operator: any, value2: any, passOnNull?: any): boolean {
  // Fix #1: passOnNull is usually a string, so casting to bool doesn't work
  if (passOnNull === 'false') {
    passOnNull = false;
  }
  if (!!passOnNull && (!value1 || !value2)) {
    return true;
  }
  const dateRegex =
    /(?=\d)^(?:(?!(?:10\D(?:0?[5-9]|1[0-4])\D(?:1582))|(?:0?9\D(?:0?[3-9]|1[0-3])\D(?:1752)))((?:0?[13578]|1[02])|(?:0?[469]|11)(?!\/31)(?!-31)(?!\.31)|(?:0?2(?=.?(?:(?:29.(?!000[04]|(?:(?:1[^0-6]|[2468][^048]|[3579][^26])00))(?:(?:(?:\d\d)(?:[02468][048]|[13579][26])(?!\x20BC))|(?:00(?:42|3[0369]|2[147]|1[258]|09)\x20BC))))))|(?:0?2(?=.(?:(?:\d\D)|(?:[01]\d)|(?:2[0-8])))))([-./])(0?[1-9]|[12]\d|3[01])\2(?!0000)((?=(?:00(?:4[0-5]|[0-3]?\d)\x20BC)|(?:\d{4}(?!\x20BC)))\d{4}(?:\x20BC)?)(?:$|(?=\x20\d)\x20))?((?:(?:0?[1-9]|1[012])(?::[0-5]\d){0,2}(?:\x20[aApP][mM]))|(?:[01]\d|2[0-3])(?::[0-5]\d){1,2})?$/;
  if (dateRegex.test(value1)) {
    value1 = Date.parse(value1);
    value2 = Date.parse(value2);
  } else if (value1 === true || value1 === false || value1 === 'true' || value1 === 'false') {
    if (value1 === 'false') value1 = false;
    if (value2 === 'false') value2 = false;
    value1 = !!value1;
    value2 = !!value2;
  } else if (!isNaN(parseFloat(value1))) {
    // Fix #2: numeric check (value1 - 0) == value1 doesn't work for whitespace-only strings
    value1 = parseFloat(value1);
    value2 = parseFloat(value2);
  }
  switch (operator) {
    case 'EqualTo':
      if (value1 == value2) return true;
      break;
    case 'NotEqualTo':
      if (value1 != value2) return true;
      break;
    case 'GreaterThan':
      if (value1 > value2) return true;
      break;
    case 'LessThan':
      if (value1 < value2) return true;
      break;
    case 'GreaterThanOrEqualTo':
      if (value1 >= value2) return true;
      break;
    case 'LessThanOrEqualTo':
      if (value1 <= value2) return true;
      break;
    case 'RegExMatch':
      return new RegExp(value2).test(value1);
      break;
    case 'NotRegExMatch':
      return !new RegExp(value2).test(value1);
  }
  return false;
}

export type ValidationElement = {
  name: string;
  label: string;
};

export type ValidationRule = {
  dateformat: string;
  mindate: string;
  maxdate: string;
  min: number;
  validateempty: 'true' | 'false';
  other: string;
  start: string;
  end: string;
  fileridfield: string;
  pattern: string;
  booleanfield: string;
  base: string;
  checkboxesselector: string;
  otherexpectedvalue: string;
  rolefield: string;
  validcodes: string;
};

jQuery.validator.setDefaults({
  ignore: ':hidden:not(.forceValidate)',
});

jQuery.extend(jQuery.validator.methods, {
  /* Xbrl valid date rule */
  xbrlvaliddate: (value: string, _: ValidationElement, rule: ValidationRule): boolean => {
    if (value && value.toString().length > 0) {
      const valueDate = moment(value, rule.dateformat, true);
      const minDate = moment(rule.mindate, rule.dateformat, true);
      const maxDate = moment(rule.maxdate, rule.dateformat, true);
      return valueDate.isValid() && minDate.isValid() && maxDate.isValid() && valueDate.isAfter(minDate) && valueDate.isBefore(maxDate);
    }

    return true;
  },

  /* Min length rule */
  minlength: function (
    this: { optional: (element: ValidationElement) => boolean },
    value: string,
    element: ValidationElement,
    rule: ValidationRule,
  ): boolean {
    if (this.optional(element) && value.length == 0) {
      return true;
    }

    return value.length >= rule.min;
  },

  /* Equals to rule */
  equalsto: (value: string, element: ValidationElement, rule: ValidationRule): boolean => {
    if (rule.validateempty !== 'true' && (!value || value.length == 0)) {
      return true;
    }

    const otherFieldControl = getValidationElementById(element, rule.other);
    if (otherFieldControl == null) {
      return false;
    }

    const otherPropertyValue = (otherFieldControl[0] as HTMLInputElement).value;
    return otherPropertyValue == value;
  },

  callotherclient: (value: string, element: ValidationElement, rule: ValidationRule): boolean => {
    if (rule.validateempty !== 'true' && (!value || value.length == 0)) {
      return true;
    }

    const otherControl = getValidationElementById(element, rule.other);

    if (!otherControl || otherControl.length == 0) {
      return false;
    }
    const name = otherControl.attr('name');
    const validator = otherControl.closest('form').data('validator');
    if (!name || !validator) {
      return false;
    }
    // Match the behaviour of jquery-validate's validators - don't validate it if it hasn't yet been touched/submitted
    if (name in validator.submitted || name in validator.invalid) {
      setTimeout(() => otherControl.valid(), 1);
    }

    return true;
  },

  endafterstart: (value: string, element: ValidationElement, rule: ValidationRule): boolean => {
    if (rule.validateempty !== 'true' && (!value || value.length == 0)) {
      return true;
    }

    const startControl = getValidationElementById(element, rule.start) as JQuery<HTMLInputElement>;
    const endControl = getValidationElementById(element, rule.end) as JQuery<HTMLInputElement>;

    if (!startControl || startControl.length == 0 || !endControl || endControl.length == 0) {
      return false;
    }

    return startControl[0].value <= endControl[0].value;
  },

  uniquefilingnamebyfiler: (value: string, _: ValidationElement, rule: ValidationRule): boolean => {
    //needs the existence of a method called populate cache and an array called baseFiling cache to store in.
    if (!window.baseFilingCache || !window.populateCache) {
      throw 'Base filing cache and populateCache must be defined.';
    }

    const filerIdField = $('#' + rule.fileridfield);

    if (filerIdField.length != 1) {
      throw 'Must have a single filer id field.';
    }

    const filerId = filerIdField.val();

    if (!window.baseFilingCache) {
      window.populateCache(() => undefined, false);
    }

    if (window.baseFilingCache.length == 0) {
      return true;
    }

    const matchingEntries = $.grep(window.baseFilingCache, (element) => {
      return element.label.toLocaleLowerCase() == value.toLocaleLowerCase() && element.filerId == filerId;
    });

    return matchingEntries.length == 0;
  },

  clientuniquefilername: (value: string): boolean => {
    //needs the existence of a list of filers called 'filers' - otherwise we are validating serverside.
    if (!window.filers) {
      return true;
    }

    const matchingEntries = $.grep(window.filers, (element) => {
      return element.label == value;
    });

    return matchingEntries.length == 0;
  },

  wellformedcompanywebsite: (value: string): boolean => isWellFormedCompanyWebsite(value),
  wellformedcompanyprefix: (value: string): boolean => isWellFormedCompanyPrefix(value),
  unreservedcompanyprefix: (value: string): boolean => isUnreservedCompanyPrefix(value),

  shrequiredif: (_: string, __: ValidationElement, rule: ValidationRule): boolean => {
    const controlToCheck = $('#' + rule.base + ',[name=' + rule.base + ']:checked');
    const otherControl = $('#' + rule.other + ',[name=' + rule.other + ']:checked');

    if (otherControl.val() == rule.otherexpectedvalue) {
      const value = controlToCheck.val() as string | undefined;
      return !!value && value.length > 0;
    }

    return true;
  },

  validrole: (value: string, _: ValidationElement, rule: ValidationRule): boolean => {
    const roleValue = $('#' + rule.rolefield).val();
    if (roleValue != window.SITE_ADMIN_ROLE && roleValue != window.DOMAIN_ADMIN_ROLE) {
      return value != null && value.length > 0;
    }

    return true;
  },

  validcrn: function (
    this: { settings: { messages: { [key: string]: { validcrn: string } } } },
    value: string,
    element: ValidationElement,
    rule: ValidationRule,
  ): boolean {
    if (!value || value.length == 0) {
      return true;
    }

    const otherFieldControl = getValidationElementById(element, rule.other);
    if (typeof window.regexArray == 'object' && otherFieldControl != null && otherFieldControl.length > 0) {
      const otherFieldVal = otherFieldControl.val() as string;
      if (!otherFieldVal || otherFieldVal === '') {
        return true;
      }

      const regex = new RegExp(window.regexArray[otherFieldVal]);
      if (value.search(regex) == -1) {
        if (typeof window.errorMessageArray == 'object') {
          this.settings.messages[element.name].validcrn = window.errorMessageArray[otherFieldVal];
        } else {
          this.settings.messages[element.name].validcrn = 'Validation error message not found.';
        }
        return false;
      }

      return true;
    } else {
      this.settings.messages[element.name].validcrn = 'An error has occurred during validation.';
      return false;
    }
  },

  noclientvalidation: (): boolean => {
    return true;
  },

  /* SHRequired rule */
  shrequired: (value: string): boolean => {
    return !!value && value.length > 0;
  },

  /* SHRegex rule */
  shregex: (value: string, _: ValidationElement, rule: ValidationRule): boolean => {
    if (!value || value.length == 0) {
      return true;
    }
    return !!value.match(new RegExp(rule.pattern));
  },

  shrequiredskip: (value: string, _: ValidationElement, rule: ValidationRule): boolean => {
    const cleanValue = value.toLowerCase().replace(new RegExp(rule.pattern, 'g'), '');

    const trimmedValue = cleanValue.replace(/^\s+|\s+$/g, '');

    return trimmedValue.length > 0;
  },

  /* SHRegexPassword rule */
  shregexpassword: (value: string, _: ValidationElement, rule: ValidationRule): boolean => {
    if (!value || value.length == 0) {
      return true;
    }
    return !!value.match(new RegExp(rule.pattern));
  },

  requiredifnotempty: (value: string, _: ValidationElement, rule: ValidationRule): boolean => {
    const otherControl = $('#' + rule.other + ',[name=' + rule.other + ']:checked');

    if (otherControl.val() != null && otherControl.val() != '') {
      return !!value && value.length > 0;
    }

    return true;
  },

  iftruethenendafterstart: (value: string, element: ValidationElement, rule: ValidationRule): boolean => {
    const booleanField = $('[name=' + rule.booleanfield + ']');

    if (!booleanField.is('[type="checkbox"]')) {
      return false;
    }

    if (!booleanField.is(':checked')) {
      return true;
    }

    return $.validator.methods.endafterstart(value, element, rule);
  },

  iftruethencallotherclient: (value: string, element: ValidationElement, rule: ValidationRule): boolean => {
    const booleanField = $('[name=' + rule.booleanfield + ']');

    if (!booleanField.is('[type="checkbox"]')) {
      return false;
    }

    if (!booleanField.is(':checked')) {
      return true;
    }

    return $.validator.methods.callotherclient(value, element, rule);
  },

  iftruethenxbrlvaliddate: (value: string, element: ValidationElement, rule: ValidationRule): boolean => {
    const booleanField = $('[name=' + rule.booleanfield + ']');

    if (!booleanField.is('[type="checkbox"]')) {
      return false;
    }

    if (!booleanField.is(':checked')) {
      return true;
    }

    return $.validator.methods.xbrlvaliddate(value, element, rule);
  },

  atleastonechecked: (_: string, __: ValidationElement, rule: ValidationRule): boolean => {
    return (
      $(rule.checkboxesselector).filter((_, e) => {
        return $(e).is(':checked');
      }).length > 0
    );
  },

  noduplicateentitytypes: (value: string): boolean => {
    return !value?.toString().length || $(`.entity-type-id option:selected[value=${value}]`).length === 1;
  },
});

function getValidationElementById(element: ValidationElement, id: string) {
  //in some cases there is a prefix on all the ids, this isn't known server side and therefore we need to get it from the element we are originally validating.

  const elementId = $(element).prop('id')!;
  const prefix = elementId.substring(0, elementId.lastIndexOf('_') + 1);

  return $('#' + id + ',#' + prefix + id);
}

type Options = { rules: { [name: string]: unknown }; params: unknown; message: string; messages: { [name: string]: string } };

function setValidationValues(options: Options, ruleName: string) {
  options.rules[ruleName] = options.params;
  if (options.message) {
    options.messages[ruleName] = options.message;
  }
}

function addUnobtrusiveAdapter(validatorName: string, parameterNames: string[]) {
  jQuery.validator.unobtrusive.adapters.add(validatorName, parameterNames, (options: Options) => {
    setValidationValues(options, validatorName);
  });
}
